import { Button, Paper, Stack, TextField, Typography } from "@mui/material";
import { FormEvent, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import SinglePage from "../SinglePage/SinglePage";
import { resetPassword } from "../../../services/userApi";
import { COLORS } from "../../../values/colors";

const PasswordReset = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token")?.trim() ?? "";
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isTokenMissing = useMemo(() => token.length === 0, [token]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setErrorMessage(null);
    setSuccessMessage(null);

    if (isTokenMissing) {
      setErrorMessage("Der Link ist unvollständig. Bitte fordere einen neuen Passwort-Reset an.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setErrorMessage("Die Passwörter stimmen nicht überein.");
      return;
    }

    setIsSubmitting(true);

    try {
      await resetPassword({ token, neuesPasswort: newPassword });
      setSuccessMessage("Dein Passwort wurde aktualisiert. Du kannst dich jetzt mit dem neuen Passwort anmelden.");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : "Passwort konnte nicht zurückgesetzt werden.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <SinglePage
      title="Passwort zurücksetzen"
      heroBanner={<></>}
      content={
        <Stack alignItems="center" sx={{ width: "100%", padding: "8rem 1rem 5rem" }}>
          <Paper
            elevation={0}
            sx={{
              width: "min(100%, 34rem)",
              padding: { xs: "1.4rem", md: "2rem" },
              borderRadius: "1.8rem",
              color: COLORS.white,
              background:
                "radial-gradient(circle at top left, rgba(181,115,76,0.16), transparent 34%), linear-gradient(180deg, rgba(20,20,20,0.98), rgba(10,10,10,0.98))",
              border: "1px solid rgba(181,115,76,0.24)",
              boxShadow: "0 28px 70px rgba(0,0,0,0.4)",
            }}
          >
            <Stack component="form" spacing={2} onSubmit={handleSubmit}>
              <Typography variant="overline" sx={{ color: COLORS.apronBrown, letterSpacing: "0.18em" }}>
                Benutzerkonto
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                Passwort zurücksetzen
              </Typography>
              <Typography sx={{ color: "rgba(255,255,255,0.74)", lineHeight: 1.7 }}>
                Vergib ein neues Passwort für dein Konto. Das Passwort muss mindestens 12 Zeichen lang sein
                sowie Groß-, Kleinbuchstaben und Zahlen enthalten.
              </Typography>
              <TextField
                label="Neues Passwort"
                type="password"
                value={newPassword}
                onChange={(event) => setNewPassword(event.target.value)}
                required
                fullWidth
                disabled={Boolean(successMessage) || isTokenMissing}
                InputLabelProps={{ sx: { color: "rgba(255,255,255,0.55)" } }}
                InputProps={{
                  sx: {
                    color: COLORS.white,
                    borderRadius: "1rem",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "rgba(255,255,255,0.15)",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "rgba(181,115,76,0.4)",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: COLORS.apronBrown,
                    },
                  },
                }}
              />
              <TextField
                label="Passwort wiederholen"
                type="password"
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                required
                fullWidth
                disabled={Boolean(successMessage) || isTokenMissing}
                InputLabelProps={{ sx: { color: "rgba(255,255,255,0.55)" } }}
                InputProps={{
                  sx: {
                    color: COLORS.white,
                    borderRadius: "1rem",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "rgba(255,255,255,0.15)",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "rgba(181,115,76,0.4)",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: COLORS.apronBrown,
                    },
                  },
                }}
              />
              {errorMessage ? (
                <Typography sx={{ color: "#ff9f9f" }}>{errorMessage}</Typography>
              ) : null}
              {successMessage ? (
                <Typography sx={{ color: COLORS.creamWhite }}>{successMessage}</Typography>
              ) : null}
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2} justifyContent="flex-end">
                <Button
                  type="button"
                  variant="outlined"
                  onClick={() => navigate("/")}
                  sx={{
                    color: "rgba(255,255,255,0.9)",
                    borderRadius: "999px",
                    borderColor: "rgba(255,255,255,0.18)",
                    textTransform: "none",
                    paddingInline: "1.2rem",
                  }}
                >
                  Zur Startseite
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isSubmitting || Boolean(successMessage) || isTokenMissing}
                  sx={{
                    borderRadius: "999px",
                    paddingInline: "1.4rem",
                    background: `linear-gradient(135deg, ${COLORS.apronBrown}, ${COLORS.apronBrownDark})`,
                    textTransform: "none",
                  }}
                >
                  {isSubmitting ? "Speichere..." : "Passwort ändern"}
                </Button>
              </Stack>
            </Stack>
          </Paper>
        </Stack>
      }
    />
  );
};

export default PasswordReset;
